
import React from "react";
import { Edit, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { CalendarViewProps } from "../types";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLanguage } from "@/components/LanguageContext";

export const MonthView = ({
  date,
  events,
  handleDateClick,
  handleEventClick,
  handleDeleteEvent,
  getEventColor,
  setDate
}: CalendarViewProps) => {
  const { t, language } = useLanguage();
  
  const todayEvents = events.filter(
    (event) => format(new Date(event.start_time), "yyyy-MM-dd") === format(date, "yyyy-MM-dd")
  );

  const upcomingEvents = events
    .filter((event) => new Date(event.start_time) > new Date())
    .sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
    .slice(0, 5);

  // Use the correct locale based on language
  const dateLocale = language === 'pt' ? pt : undefined;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-7 gap-6">
      <div className="lg:col-span-5">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(newDate) => newDate && setDate(newDate)}
          onDayClick={handleDateClick}
          className="w-full rounded-md border border-gray-300 dark:border-gray-700"
          showOutsideDays={true}
        />
        
        <div className="mt-6">
          <h3 className="font-semibold mb-4 dark:text-gray-200">
            {language === 'en' ? 'Daily Events' : 'Eventos do Dia'}
          </h3>
          <ScrollArea className="h-[300px]">
            <div className="space-y-2 pr-4">
              {todayEvents.map((event) => {
                // Determinar o título a ser exibido
                const studentName = event.students?.first_name && event.students?.last_name 
                  ? `${event.students.first_name} ${event.students.last_name}`
                  : null;
                
                const displayTitle = event.event_type === 'other' 
                  ? event.title 
                  : (studentName || (language === 'en' ? "Individual Training" : "Treino Individual"));
                
                return (
                  <div
                    key={event.id}
                    className={`p-3 rounded-lg cursor-pointer hover:opacity-90 transition-all ${getEventColor(
                      event.event_type
                    )} backdrop-blur-sm`}
                    onClick={() => handleEventClick(event)}
                  >
                    <div className="flex justify-between">
                      <div>
                        <div className="font-semibold">{displayTitle}</div>
                        <div className="text-sm">
                          {format(new Date(event.start_time), "HH:mm")} - {format(new Date(event.end_time), "HH:mm")}
                        </div>
                        {event.event_type !== 'other' && studentName && event.title && event.title !== "Treino Individual" && event.title !== "Individual Training" && (
                          <div className="text-sm opacity-75 dark:text-gray-300">
                            {event.title}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEventClick(event);
                          }}
                          className="p-1 hover:bg-white/20 dark:text-gray-300 dark:hover:text-white rounded"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteEvent(event);
                          }}
                          className="p-1 hover:bg-white/20 dark:text-gray-300 dark:hover:text-white rounded"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      </div>
      
      <div className="lg:col-span-2">
        <div className="bg-card dark:bg-gray-800 rounded-lg p-4 sticky top-4 border border-gray-300 dark:border-gray-700">
          <h3 className="font-semibold mb-4 dark:text-gray-200">
            {language === 'en' ? 'Upcoming Events' : 'Próximos Eventos'}
          </h3>
          <ScrollArea className="h-[600px]">
            <div className="space-y-2 pr-4">
              {upcomingEvents.map((event) => {
                // Determinar o título a ser exibido
                const studentName = event.students?.first_name && event.students?.last_name 
                  ? `${event.students.first_name} ${event.students.last_name}`
                  : null;
                
                const displayTitle = event.event_type === 'other' 
                  ? event.title 
                  : (studentName || (language === 'en' ? "Individual Training" : "Treino Individual"));
                
                return (
                  <div
                    key={event.id}
                    className={`p-3 rounded-lg cursor-pointer hover:opacity-90 transition-all ${getEventColor(
                      event.event_type
                    )} backdrop-blur-sm`}
                    onClick={() => handleEventClick(event)}
                  >
                    <div className="flex justify-between">
                      <div>
                        <div className="text-sm font-medium dark:text-gray-300">
                          {format(new Date(event.start_time), language === 'pt' ? "d 'de' MMMM" : "MMMM d", { locale: dateLocale })}
                        </div>
                        <div className="font-semibold">{displayTitle}</div>
                        <div className="text-sm">
                          {format(new Date(event.start_time), "HH:mm")}
                        </div>
                        {event.event_type !== 'other' && studentName && event.title && event.title !== "Treino Individual" && event.title !== "Individual Training" && (
                          <div className="text-sm opacity-75 dark:text-gray-300">
                            {event.title}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEventClick(event);
                          }}
                          className="p-1 hover:bg-white/20 dark:text-gray-300 dark:hover:text-white rounded"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteEvent(event);
                          }}
                          className="p-1 hover:bg-white/20 dark:text-gray-300 dark:hover:text-white rounded"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
};
